import { z } from 'zod';

/**
 * CSV import.
 *
 * Two steps, deliberately: **preview** parses the file, applies the column
 * mapping and validates every row, reporting what would happen; **commit**
 * does it. Importing a thousand rows and discovering on row 900 that a column
 * was mapped wrong is the single worst experience a CRM can offer, so the
 * preview is not optional and the commit reuses its exact parse.
 */
export const IMPORT_DATASETS = ['leads', 'accounts', 'contacts'] as const;
export type ImportDataset = (typeof IMPORT_DATASETS)[number];

/** Bounded so one upload cannot exhaust the process's memory. */
export const MAX_IMPORT_ROWS = 5000;
export const MAX_IMPORT_BYTES = 5 * 1024 * 1024;

export const importPreviewSchema = z.object({
  dataset: z.enum(IMPORT_DATASETS),
  /** The raw file contents. */
  csv: z.string().min(1, 'The file is empty').max(MAX_IMPORT_BYTES),
  /**
   * CSV header → record field. Omitted, the server guesses from the headers,
   * which is right often enough to be worth offering and is always shown back
   * for confirmation.
   */
  mapping: z.record(z.string(), z.string()).optional(),
  /**
   * Skip a row whose email matches an existing record instead of creating a
   * duplicate. On by default: a re-uploaded file should not double the data.
   */
  skipDuplicates: z.coerce.boolean().default(true),
});
export type ImportPreviewInput = z.infer<typeof importPreviewSchema>;

export const importCommitSchema = importPreviewSchema.extend({
  /** Assign every imported record to one business manager; omitted, the importer owns them. */
  businessManagerId: z.uuid().optional(),
});
export type ImportCommitInput = z.infer<typeof importCommitSchema>;

export interface ImportRowIssue {
  /** 1-based, counting the header as row 1 — what the spreadsheet shows. */
  row: number;
  field: string;
  message: string;
}

export interface ImportPreview {
  dataset: ImportDataset;
  /** The headers found in the file, in order. */
  headers: string[];
  /** The mapping that will be used — the caller's, or the server's guess. */
  mapping: Record<string, string>;
  /** Fields the target record type accepts, for the mapping UI. */
  targetFields: { name: string; label: string; required: boolean }[];
  totalRows: number;
  validRows: number;
  duplicateRows: number;
  issues: ImportRowIssue[];
  /** The first few parsed records, so the mapping can be eyeballed. */
  sample: Record<string, unknown>[];
}

export interface ImportResult {
  dataset: ImportDataset;
  created: number;
  skipped: number;
  /** Rows that failed validation and were not imported. */
  failed: number;
  issues: ImportRowIssue[];
}

/**
 * Parses CSV text into rows.
 *
 * Hand-written rather than a dependency, because the grammar that matters is
 * small and entirely specified by RFC 4180: quoted fields may contain commas,
 * newlines and doubled quotes. A library would bring configuration, encoding
 * detection and a changelog for a hundred lines of state machine.
 *
 * Returns rows as arrays; mapping to fields is the caller's job.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let index = 0;

  // A BOM at the start is what Excel writes; left in place it becomes part of
  // the first header and every mapping against it silently fails.
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  const endField = (): void => {
    row.push(field);
    field = '';
  };
  const endRow = (): void => {
    endField();
    // A trailing newline would otherwise add a row of one empty field.
    if (row.length > 1 || row[0] !== '') rows.push(row);
    row = [];
  };

  while (index < source.length) {
    const char = source[index]!;

    if (inQuotes) {
      if (char === '"') {
        // A doubled quote inside a quoted field is one literal quote.
        if (source[index + 1] === '"') {
          field += '"';
          index += 2;
          continue;
        }
        inQuotes = false;
        index += 1;
        continue;
      }
      field += char;
      index += 1;
      continue;
    }

    if (char === '"' && field === '') {
      inQuotes = true;
      index += 1;
      continue;
    }
    if (char === ',') {
      endField();
      index += 1;
      continue;
    }
    if (char === '\r') {
      // CRLF and a lone CR both end the row.
      if (source[index + 1] === '\n') index += 1;
      endRow();
      index += 1;
      continue;
    }
    if (char === '\n') {
      endRow();
      index += 1;
      continue;
    }

    field += char;
    index += 1;
  }

  if (field !== '' || row.length > 0) endRow();
  return rows;
}

/**
 * Guesses which record field a CSV header refers to.
 *
 * Compares on letters and digits only, so "First Name", "first_name" and
 * "FIRSTNAME" all land on `firstName`. A guess is always shown back for
 * confirmation, so being wrong costs a correction rather than bad data.
 */
export function guessMapping(
  headers: readonly string[],
  targetFields: readonly { name: string; aliases?: readonly string[] }[],
): Record<string, string> {
  const normalise = (value: string): string => value.toLowerCase().replaceAll(/[^a-z0-9]/gu, '');

  const byNormalised = new Map<string, string>();
  // Names first, then aliases on top: an alias is a deliberate "this header
  // means that field", so it beats another field that merely shares the name
  // (a lead's "Mobile" column is its `phone`, not the secondary `mobile`).
  for (const field of targetFields) byNormalised.set(normalise(field.name), field.name);
  for (const field of targetFields) {
    for (const alias of field.aliases ?? []) byNormalised.set(normalise(alias), field.name);
  }

  const mapping: Record<string, string> = {};
  const claimed = new Set<string>();

  for (const header of headers) {
    const match = byNormalised.get(normalise(header));
    // One CSV column per field: two columns both called "Email" would
    // otherwise silently overwrite one another.
    if (match && !claimed.has(match)) {
      mapping[header] = match;
      claimed.add(match);
    }
  }

  return mapping;
}
